// Verwaltung › Anbindungen: technische Zugänge mit API-Schlüsseln, Webhooks mit Zustellprotokoll, Importläufe.
// Geheimnisse (API-Schlüssel, Webhook-Geheimnis) erscheinen genau einmal in der Antwort der anlegenden Aktion;
// gespeichert ist nur ihr Hash bzw. die verschlüsselte Form. Solche Aktionen nehmen keinen Idempotenzschlüssel an
// (sonst läge der Klartext im Wiederholungsprotokoll) – siehe server/admin/support.ts (refuseReplay).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'

const zTimestamp = z.string()

export const zApiToken = z.object({
  id: zId,
  name: z.string(),
  /** Anfang des Schlüssels zur Wiedererkennung ('mxo_AbCdEfGh…') – nie der ganze Schlüssel */
  prefix: z.string().nullable(),
  createdAt: zTimestamp,
  createdByName: z.string().nullable(),
  expiresAt: zTimestamp.nullable(),
  lastUsedAt: zTimestamp.nullable(),
  revokedAt: zTimestamp.nullable(),
  state: z.enum(['active', 'expired', 'revoked']),
  stateLabel: z.string(),
})

export const zTechnicalAccount = z.object({
  membershipId: zId,
  name: z.string(),
  status: z.enum(['active', 'disabled', 'invited']),
  /** Rollen = Umfang der Schlüssel dieses Zugangs */
  roles: z.array(z.object({ assignmentId: zId, roleId: zId, roleName: z.string(), locationId: zId.nullable(), locationLabel: z.string() })),
  /** was der Zugang darf, in Worten (Rechte seiner Rollen) */
  scopes: z.array(z.string()),
  /** Aktionen der öffentlichen API, die dieser Zugang aufrufen darf */
  publicActions: z.array(z.string()),
  tokens: z.array(zApiToken),
})

export const zWebhook = z.object({
  id: zId,
  name: z.string(),
  url: z.string(),
  /** Ereignisarten oder Muster ('sales.*', '*') */
  eventTypes: z.array(z.string()),
  active: z.boolean(),
  hasSecret: z.boolean(),
  createdAt: zTimestamp,
  /** Zustellungen der letzten 7 Tage */
  recent: z.object({ delivered: z.int().nonnegative(), pending: z.int().nonnegative(), failed: z.int().nonnegative() }),
  lastDeliveryAt: zTimestamp.nullable(),
  version: z.int(),
})

export const integrationOverview = defineContract({
  name: 'platform.integration.overview',
  title: 'Anbindungen anzeigen',
  description: 'Technische Zugänge mit ihren API-Schlüsseln (ohne Klartext), Webhooks und die Ereignisarten, die sich abonnieren lassen.',
  llm: {
    description:
      'Liefert die Anbindungen des Betriebs: technische Zugänge mit Umfang und Schlüsseln (Name, letzte Nutzung, Zustand – ' +
      'nie der Schlüssel selbst) und Webhooks mit Ziel und Ereignissen. Verwenden für „Wann hat die Bestell-App zuletzt ' +
      'zugegriffen?“.',
    keywords: ['API', 'Schlüssel', 'Schnittstelle', 'Webhook', 'Bestell-App', 'Anbindung', 'Token'],
  },
  input: z.strictObject({}),
  output: z.object({
    accounts: z.array(zTechnicalAccount),
    webhooks: z.array(zWebhook),
    /** Ereignisarten, die das System kennt */
    events: z.array(z.object({ name: z.string(), title: z.string(), module: z.string() })),
    /** Aktionen der öffentlichen API (Beschreibung: GET /api/public/v1/actions) */
    publicApi: z.object({ path: z.string(), actions: z.array(z.object({ name: z.string(), title: z.string() })) }),
  }),
  permission: 'platform.integration.read',
  risk: 'read',
})

export const technicalAccountCreate = defineContract({
  name: 'platform.apiAccount.create',
  title: 'Technischen Zugang anlegen',
  description:
    'Legt einen technischen Zugang für eine Schnittstelle an. Seine Rollen bestimmen, was seine API-Schlüssel dürfen – höchstens, was Sie selbst dürfen.',
  llm: { description: 'Legt einen technischen Zugang an. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    name: z.string().trim().min(2, { error: 'Bitte geben Sie dem Zugang einen Namen.' }).max(80),
    roles: z.array(z.strictObject({ roleId: zId, locationId: zId.nullable() })).min(1, { error: 'Bitte wählen Sie mindestens eine Rolle.' }).max(20),
  }),
  output: z.object({ membershipId: zId }),
  permission: 'platform.integration.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const apiTokenCreate = defineContract({
  name: 'platform.apiToken.create',
  title: 'API-Schlüssel ausstellen',
  description:
    'Stellt einen API-Schlüssel für einen technischen Zugang aus. Der Schlüssel erscheint genau einmal – gespeichert wird nur sein Hash.',
  llm: { description: 'Stellt einen API-Schlüssel aus. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    membershipId: zId,
    name: z.string().trim().min(2, { error: 'Bitte geben Sie dem Schlüssel einen Namen.' }).max(80),
    /** Gültigkeit in Tagen; ohne Angabe läuft der Schlüssel nicht ab */
    validDays: z.int().min(1, { error: 'Mindestens ein Tag.' }).max(3650, { error: 'Höchstens zehn Jahre.' }).optional(),
  }),
  output: z.object({
    id: zId,
    /** der Schlüssel im Klartext – nur in dieser Antwort */
    secret: z.string(),
    prefix: z.string(),
    expiresAt: zTimestamp.nullable(),
  }),
  permission: 'platform.integration.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const apiTokenRevoke = defineContract({
  name: 'platform.apiToken.revoke',
  title: 'API-Schlüssel widerrufen',
  description: 'Widerruft einen API-Schlüssel sofort. Aufrufe damit werden abgelehnt; das lässt sich nicht zurücknehmen.',
  llm: { description: 'Widerruft einen API-Schlüssel. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ id: zId }),
  output: z.object({ revoked: z.boolean() }),
  permission: 'platform.integration.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

const zEventPattern = z
  .string()
  .trim()
  .regex(/^(\*|[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*(\.\*)?)$/, { error: 'Bitte eine Ereignisart oder ein Muster wie sales.* angeben.' })

export const webhookSave = defineContract({
  name: 'platform.webhook.save',
  title: 'Webhook speichern',
  description:
    'Legt ein Webhook-Ziel an oder ändert Name, Adresse, Ereignisse und Zustand. Beim Anlegen – und auf Wunsch neu – entsteht ein Geheimnis zur Prüfung der Signatur; es erscheint genau einmal.',
  llm: { description: 'Speichert ein Webhook-Ziel. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    id: zId.optional(),
    name: z.string().trim().min(2, { error: 'Bitte geben Sie dem Webhook einen Namen.' }).max(80),
    url: z.string().trim().min(8, { error: 'Bitte geben Sie die Adresse des Ziels ein.' }).max(500),
    eventTypes: z.array(zEventPattern).min(1, { error: 'Bitte wählen Sie mindestens ein Ereignis.' }).max(50),
    active: z.boolean().default(true),
    /** true: neues Geheimnis erzeugen (das alte gilt sofort nicht mehr) */
    rotateSecret: z.boolean().default(false),
    expectedVersion: z.int().positive().optional(),
  }),
  output: z.object({
    id: zId,
    /** das Geheimnis im Klartext – nur beim Anlegen und beim Erneuern, sonst null */
    secret: z.string().nullable(),
    version: z.int(),
  }),
  permission: 'platform.integration.manage',
  risk: 'write',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

export const webhookRemove = defineContract({
  name: 'platform.webhook.remove',
  title: 'Webhook löschen',
  description: 'Löscht ein Webhook-Ziel. Offene Zustellungen an dieses Ziel entfallen.',
  llm: { description: 'Löscht ein Webhook-Ziel. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ id: zId }),
  output: z.object({ removed: z.boolean() }),
  permission: 'platform.integration.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const DELIVERY_STATES = ['pending', 'delivered', 'failed', 'abandoned'] as const
export const DELIVERY_STATE_LABELS: Record<string, string> = {
  pending: 'Wartet',
  delivered: 'Zugestellt',
  failed: 'Gescheitert',
  abandoned: 'Aufgegeben',
}

export const zWebhookDelivery = z.object({
  id: zId,
  endpointId: zId,
  endpointName: z.string(),
  eventType: z.string(),
  eventTitle: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  attempts: z.int().nonnegative(),
  responseCode: z.int().nullable(),
  lastError: z.string().nullable(),
  createdAt: zTimestamp,
  deliveredAt: zTimestamp.nullable(),
  nextAttemptAt: zTimestamp.nullable(),
  retryable: z.boolean(),
})

export const webhookDeliveries = defineContract({
  name: 'platform.webhook.deliveries',
  title: 'Webhook-Zustellungen anzeigen',
  description: 'Das Zustellprotokoll der Webhooks: Ereignis, Ziel, Versuche, Antwort – neueste zuerst.',
  llm: {
    description:
      'Listet Zustellungen an Webhook-Ziele mit Zustand, Zahl der Versuche und Fehlertext. Verwenden für „Kommen die ' +
      'Auftragsmeldungen bei der Bestell-App an?“.',
    keywords: ['Webhook', 'Zustellung', 'gescheitert', 'Schnittstelle'],
  },
  input: listInput({
    sortBy: ['createdAt'],
    defaultSort: { by: 'createdAt', dir: 'desc' },
    filters: { endpointId: zId.optional(), status: z.enum(DELIVERY_STATES).optional() },
  }),
  output: listOutput(zWebhookDelivery),
  permission: 'platform.integration.read',
  risk: 'read',
})

export const webhookRetry = defineContract({
  name: 'platform.webhook.retry',
  title: 'Zustellung wiederholen',
  description: 'Stellt eine gescheiterte Webhook-Zustellung erneut ein.',
  llm: { description: 'Wiederholt eine gescheiterte Webhook-Zustellung. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ deliveryId: zId }),
  output: z.object({ queued: z.boolean() }),
  permission: 'platform.integration.manage',
  risk: 'write',
  effects: { securitySensitive: true, external: true },
  assistant: 'never',
})

// ---- Importläufe -----------------------------------------------------------------------------------------------------------

export const importRuns = defineContract({
  name: 'platform.import.runs',
  title: 'Importläufe anzeigen',
  description: 'Die Importläufe des Betriebs mit Zustand, Statistik und den ersten Zeilenfehlern – neueste zuerst.',
  llm: {
    description:
      'Listet Importläufe (Artikel, Kunden, Lieferanten …) mit Zustand, Zahl der übernommenen und fehlerhaften Zeilen. ' +
      'Verwenden für „Ist der Artikelimport von gestern durchgelaufen?“.',
    keywords: ['Import', 'CSV', 'Datenübernahme', 'Importlauf', 'Fehler'],
  },
  input: z.strictObject({ limit: z.int().min(1).max(100).default(30) }),
  output: z.object({
    runs: z.array(
      z.object({
        id: zId,
        key: z.string(),
        title: z.string(),
        status: z.string(),
        statusLabel: z.string(),
        dryRun: z.boolean(),
        fileName: z.string().nullable(),
        total: z.int().nonnegative(),
        imported: z.int().nonnegative(),
        skipped: z.int().nonnegative(),
        failed: z.int().nonnegative(),
        errors: z.array(z.object({ line: z.int().nullable(), column: z.string().optional(), message: z.string() })),
        createdByName: z.string().nullable(),
        createdAt: zTimestamp,
        finishedAt: zTimestamp.nullable(),
      }),
    ),
  }),
  permission: 'platform.settings.read',
  risk: 'read',
})
